/**
 * Boletim de Medição do Contrato (medição fechada) — impressão → PDF.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { BdmBrand, bdmVars } from "@/components/comercial/BdmBrand";
import { PageHeader, formatCurrency, verValores } from "@/components/pcp";
import { Printer, ArrowLeft, FileXls, Calculator, CaretDown, CaretUp, Percent } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (n, d = 2) => (n || 0).toLocaleString("pt-BR", { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtDate = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "—");

export default function BoletimMedicaoContrato() {
  const { id } = useParams();
  const [m, setM] = useState(null);
  const [emp, setEmp] = useState(null);
  const [memoria, setMemoria] = useState(false);
  const [baixando, setBaixando] = useState(false);

  useEffect(() => {
    api.get(`/medicoes/${id}`).then(async (r) => {
      setM(r.data);
      const c = await api.get(`/contratos/${r.data.contrato_id}`);
      if (c.data.empresa_executora_id) api.get("/empresas").then((e) => setEmp(e.data.find((x) => x.id === c.data.empresa_executora_id) || null));
    });
  }, [id]);

  if (!m) return <div className="page-body">Carregando…</div>;
  const fat = (l) => (l.qtd_faturada ?? l.qtd_periodo) || 0;
  const medidos = m.itens.filter((l) => l.qtd_periodo > 0 || fat(l) > 0);
  const pendentes = m.itens.filter((l) => l.qtd_pendente > 0);
  const rf = m.resumo_financeiro || {};
  const vv = verValores();
  const baixarPdf = async (comMemoria = false, comFotos = true, tipo = "pi") => {
    setBaixando(true);
    try {
      const r = await api.get(`/medicoes/${id}/pdf`, { params: { memoria: comMemoria ? 1 : 0, fotos: comFotos ? 1 : 0, tipo }, responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url;
      a.download = tipo === "reajuste" ? `Boletim_Medicao_${m.numero_exibicao}_R_Reajuste_Contrato_${m.contrato?.numero || ""}.pdf` : `Boletim_Medicao_${m.numero_exibicao}_Contrato_${m.contrato?.numero || ""}${comMemoria ? "_memoria" : ""}${comFotos ? "" : "_sem_fotos"}.pdf`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Não foi possível gerar o PDF"); } finally { setBaixando(false); }
  };
  const excel = async () => {
    const r = await api.get(`/medicoes/${id}/excel`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = `Medicao_${m.numero_exibicao}_${m.contrato?.numero}.xlsx`; a.click(); URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="no-print">
        <PageHeader testid="page-boletim-medicao" title={`Medição nº ${m.numero_exibicao}`} subtitle={`Contrato ${m.contrato?.numero} · ${m.contrato?.contratante}`}
          actions={<>
            <Link to={`/contratos/${m.contrato_id}`} className="btn btn-ghost"><ArrowLeft size={16} weight="bold" /> Voltar</Link>
            <button className="btn btn-outline" onClick={excel} data-testid="btn-boletim-excel"><FileXls size={16} weight="bold" /> Excel</button>
            <button className="btn btn-primary" onClick={() => baixarPdf(false)} disabled={baixando} data-testid="btn-boletim-pdf" title="Boletim + anexo fotográfico (por OS e item, até 4 fotos por item)"><Printer size={16} weight="bold" /> {baixando ? "Gerando…" : "Imprimir / PDF"}</button>
            <button className="btn btn-outline" onClick={() => baixarPdf(false, false)} disabled={baixando} data-testid="btn-boletim-pdf-sem-fotos" title="Somente o boletim, sem anexo fotográfico">Sem fotos</button>
            {vv && m.reajuste && <button className="btn btn-outline" onClick={() => baixarPdf(false, false, "reajuste")} disabled={baixando} data-testid="btn-boletim-pdf-reajuste" title={`Boletim R — reajuste de ${Number(m.reajuste.coeficiente_pct).toLocaleString("pt-BR", { maximumFractionDigits: 4 })}% sobre o PI`}><Percent size={16} weight="bold" /> Boletim R (reajuste)</button>}
          </>} />
      </div>

      <div className="bdm-doc" data-testid="boletim-doc" style={bdmVars(emp)}>
        <div className="bdm-header">
          <BdmBrand emp={emp} />
          <div className="bdm-title">
            <h1 data-testid="boletim-titulo">BOLETIM DE MEDIÇÃO Nº {m.numero_exibicao}</h1>
            <div className="bdm-os-num">Contrato {m.contrato?.numero}</div>
          </div>
        </div>

        <table className="bdm-info-table">
          <tbody>
            <tr><th>Contratante</th><td>{m.contrato?.contratante}</td><th>Período</th><td data-testid="boletim-periodo">{fmtDate(m.inicio)} a {fmtDate(m.fim)}</td></tr>
            {m.contrato?.objeto && <tr><th>Objeto</th><td colSpan={3}>{m.contrato.objeto}</td></tr>}
            <tr><th>Fechada em</th><td>{fmtDate(m.fechada_em)} · {m.fechada_por}</td><th>Apontamentos</th><td>{m.qtd_apontamentos}</td></tr>
            {m.observacao && <tr><th>Observação</th><td colSpan={3}>{m.observacao}</td></tr>}
          </tbody>
        </table>

        <h2 className="bdm-h2">1. Serviços Medidos no Período</h2>
        <table className="bdm-table" data-testid="boletim-itens">
          <thead><tr><th>Item</th><th>Descrição</th><th>Un</th><th style={{ textAlign: "right" }}>Contratado</th><th style={{ textAlign: "right" }}>Acum. anterior</th><th style={{ textAlign: "right" }}>Nesta medição</th><th style={{ textAlign: "right" }}>Acum. atual</th><th style={{ textAlign: "right" }}>Saldo</th>{vv && <><th style={{ textAlign: "right" }}>Preço unit.</th><th style={{ textAlign: "right" }}>Valor</th></>}</tr></thead>
          <tbody>
            {medidos.map((l) => (
              <tr key={l.id}>
                <td className="mono">{l.codigo}</td><td>{l.descricao}</td><td>{l.unidade}</td>
                <td style={{ textAlign: "right" }} className="mono">{fmt(l.qtd_total ?? l.qtd_contratada)}{l.qtd_aditivada > 0 && <div style={{ fontSize: "0.6rem", color: "#71717A" }}>c/ aditivo</div>}</td>
                <td style={{ textAlign: "right" }} className="mono">{fmt(l.acumulado_anterior)}</td>
                <td style={{ textAlign: "right" }} className="mono"><strong>{fmt(fat(l))}</strong>{Math.abs(fat(l) - (l.qtd_periodo || 0)) > 0.0001 && <div style={{ fontSize: "0.62rem", color: "#71717A" }}>físico no período {fmt(l.qtd_periodo)}</div>}{l.qtd_pendente > 0 && <div style={{ fontSize: "0.62rem", color: "#DC2626" }}>{fmt(l.qtd_pendente)} sem cobertura</div>}</td>
                <td style={{ textAlign: "right" }} className="mono">{fmt(l.acumulado_atual)}</td>
                <td style={{ textAlign: "right" }} className="mono">{fmt(l.saldo)}</td>
                {vv && <><td style={{ textAlign: "right" }} className="mono">{formatCurrency(l.preco_unitario)}</td>
                <td style={{ textAlign: "right" }} className="mono">{formatCurrency(l.valor_periodo)}{(l.faixas?.length > 1 || (l.faixas?.[0] && l.faixas[0].origem !== "Contrato")) && <div style={{ fontSize: "0.62rem", color: "#71717A", fontWeight: 400 }}>{l.faixas.map((f) => `${f.origem}: ${(f.qtd || 0).toLocaleString("pt-BR")} × ${formatCurrency(f.preco)}`).join(" · ")}</div>}</td></>}
              </tr>
            ))}
            {vv && <tr className="bdm-total-row"><td colSpan={9}><strong>VALOR DESTA MEDIÇÃO</strong></td><td style={{ textAlign: "right" }} className="mono" data-testid="boletim-total"><strong>{formatCurrency(m.valor_total)}</strong></td></tr>}
            {pendentes.length > 0 && (
              <tr data-testid="boletim-pendentes"><td colSpan={10} style={{ fontSize: "0.72rem", color: "#DC2626" }}>
                <strong>Executado sem cobertura contratual (não faturado nesta medição):</strong> {pendentes.map((l) => `item ${l.codigo}: ${fmt(l.qtd_pendente)} ${l.unidade || ""}`).join(" · ")}. Depende de reprogramação/aditivo formalizado.
              </td></tr>
            )}
          </tbody>
        </table>

        <h2 className="bdm-h2">2. Detalhamento por Ordem de Serviço</h2>
        <table className="bdm-table" data-testid="boletim-por-os">
          <thead><tr><th>Item</th><th>Descrição</th><th>OS</th><th>Rodovia</th><th style={{ textAlign: "right" }}>Quantidade</th>{vv && <th style={{ textAlign: "right" }}>Valor</th>}</tr></thead>
          <tbody>
            {medidos.flatMap((l) => (l.por_os || []).map((p) => (
              <tr key={l.id + p.os_id}><td className="mono">{l.codigo}</td><td>{l.descricao}</td><td className="mono">{p.numero}</td><td>{p.rodovia || "—"}</td><td style={{ textAlign: "right" }} className="mono">{fmt(p.qtd)}</td>{vv && <td style={{ textAlign: "right" }} className="mono">{formatCurrency(l.qtd_periodo ? (p.qtd / l.qtd_periodo) * (l.valor_periodo || 0) : 0)}</td>}</tr>
            )))}
          </tbody>
        </table>

        {vv && <>
        <h2 className="bdm-h2">3. Resumo Financeiro do Contrato</h2>
        <table className="bdm-info-table">
          <tbody>
            <tr>
              <th>Valor contratado</th>
              <td className="mono" data-testid="boletim-rf-contratado">
                {formatCurrency(rf.valor_total_contratado)}
                {rf.valor_aditivos > 0 && <div style={{ fontSize: "0.68rem", color: "#71717A" }}>contrato {formatCurrency(rf.valor_contrato)} + {rf.qtd_aditivos} aditivo(s) {formatCurrency(rf.valor_aditivos)}</div>}
              </td>
              <th>Acumulado até esta medição</th>
              <td className="mono" data-testid="boletim-rf-acumulado">{formatCurrency(rf.acumulado_coberto)}</td>
            </tr>
            <tr>
              <th>Esta medição</th><td className="mono"><strong>{formatCurrency(m.valor_total)}</strong></td>
              <th>Saldo contratual</th>
              <td className="mono" data-testid="boletim-rf-saldo">{formatCurrency(rf.saldo)} ({rf.percentual_executado == null ? "0" : fmt(rf.percentual_executado, 1)}% executado)</td>
            </tr>
            {rf.excedente_valor > 0 && (
              <tr data-testid="boletim-rf-excedente">
                <th style={{ color: "#DC2626" }}>Executado sem cobertura contratual</th>
                <td className="mono" style={{ color: "#DC2626" }}>{formatCurrency(rf.excedente_valor)}</td>
                <td colSpan={2} style={{ fontSize: "0.72rem", color: "#71717A" }}>
                  Não entra no acumulado nem no saldo · {rf.excedente_itens.map((e) => `${e.codigo}: ${fmt(e.qtd)} ${e.unidade}`).join(" · ")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div style={{ fontSize: "0.68rem", color: "#A1A1AA", marginTop: 4 }}>Preços: itens do contrato original ao preço contratual; quantidades cobertas por aditivo ao preço próprio de cada aditivo. Excedentes acima da cobertura são informados à parte.</div>

        </>}
        <div className="bdm-assinaturas">
          <div className="bdm-assinatura"><div className="bdm-linha-ass" />{emp?.razao_social || emp?.nome || "Contratada"}<br /><span style={{ color: "#71717A", fontSize: "0.72rem" }}>Responsável técnico</span></div>
          <div className="bdm-assinatura"><div className="bdm-linha-ass" />Fiscalização<br /><span style={{ color: "#71717A", fontSize: "0.72rem" }}>{m.contrato?.contratante}</span></div>
          <div className="bdm-assinatura"><div className="bdm-linha-ass" />Gestor do contrato<br /><span style={{ color: "#71717A", fontSize: "0.72rem" }}>data ___/___/______</span></div>
        </div>
      </div>

      {vv && <div className="no-print pcp-card" style={{ margin: "16px auto 32px", maxWidth: 1100, padding: 16 }} data-testid="boletim-memoria">
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => setMemoria(!memoria)} data-testid="boletim-memoria-toggle" aria-expanded={memoria}>
          <Calculator size={16} weight="bold" /> Memória de cálculo e conferência (uso interno) {memoria ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
        </button>
        {memoria && (
          <div style={{ marginTop: 12, fontSize: "0.82rem" }}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              <button type="button" className="btn btn-outline btn-sm" onClick={() => baixarPdf(true)} disabled={baixando} data-testid="btn-boletim-pdf-memoria"><Printer size={14} weight="bold" /> PDF com anexo de memória</button>
              <span style={{ color: "#71717A", fontSize: "0.75rem", alignSelf: "center" }}>O boletim do cliente não inclui este bloco.</span>
            </div>
            <div className="overline" style={{ marginBottom: 6 }}>Faixas por item (contrato → aditivos → excedente)</div>
            <table className="pcp-table" data-testid="boletim-memoria-faixas">
              <thead><tr><th>Item</th><th>Descrição</th><th>Faixa</th><th style={{ textAlign: "right" }}>Qtd</th><th style={{ textAlign: "right" }}>Preço</th><th style={{ textAlign: "right" }}>Valor</th></tr></thead>
              <tbody>
                {medidos.flatMap((l) => (l.faixas || []).map((f, i) => (
                  <tr key={`${l.id}-${i}`}><td className="font-mono">{l.codigo}</td><td>{l.descricao}</td><td>{f.origem}</td><td className="font-mono" style={{ textAlign: "right" }}>{fmt(f.qtd, 4)}</td><td className="font-mono" style={{ textAlign: "right" }}>{formatCurrency(f.preco)}</td><td className="font-mono" style={{ textAlign: "right" }}>{formatCurrency(f.valor)}</td></tr>
                )))}
              </tbody>
            </table>
            <div className="overline" style={{ margin: "16px 0 6px" }}>Conferência de arredondamento</div>
            <div data-testid="boletim-conferencia">
              Acumulado FIFO <strong className="font-mono">{formatCurrency(rf.acumulado_coberto + (rf.excedente_valor || 0))}</strong> × soma das {m.qtd_medicoes_ate_esta} medições fechadas até esta <strong className="font-mono">{formatCurrency(rf.soma_medicoes_ate_esta)}</strong> → diferença <strong className="font-mono" data-testid="boletim-conferencia-diferenca">{formatCurrency(rf.conferencia?.diferenca_total)}</strong>
              <div style={{ color: "#71717A", fontSize: "0.75rem", marginTop: 4 }}>{rf.conferencia?.explicacao}</div>
            </div>
            {rf.conferencia?.itens?.length > 0 && (
              <table className="pcp-table" style={{ marginTop: 8 }} data-testid="boletim-conferencia-itens">
                <thead><tr><th>Item</th><th>Descrição</th><th style={{ textAlign: "right" }}>Σ medições</th><th style={{ textAlign: "right" }}>Acumulado FIFO</th><th style={{ textAlign: "right" }}>Diferença</th></tr></thead>
                <tbody>
                  {rf.conferencia.itens.map((c) => (
                    <tr key={c.codigo}><td className="font-mono">{c.codigo}</td><td>{c.descricao}</td><td className="font-mono" style={{ textAlign: "right" }}>{formatCurrency(c.soma_medicoes)}</td><td className="font-mono" style={{ textAlign: "right" }}>{formatCurrency(c.acumulado_fifo)}</td><td className="font-mono font-semibold" style={{ textAlign: "right", color: "#DC2626" }}>{formatCurrency(c.diferenca)}</td></tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>}
    </>
  );
}
